import { message } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/Auth'

interface DocumentFormData {
  idCard: string | null
  houseRegistration: string | null
  condoOwnership: string | null
  condoJuristic: string | null
  bankBook: string | null
}

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  minRentalDuration: '1month' | '3-6months' | '1year' | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  agentFee: string
  documents: DocumentFormData
}

interface Step4Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
  isSubmittingDraft?: boolean
}

const Step4: React.FC<Step4Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
  isSubmittingDraft = false,
}) => {
  const [progress, setProgress] = useState(0)
  const [uploadingField, setUploadingField] = useState<string | null>(null)
  const [dragOverField, setDragOverField] = useState<string | null>(null)
  const { userLoggedIn } = useAuth()

  const idCardRef = useRef<HTMLInputElement>(null)
  const houseRegistrationRef = useRef<HTMLInputElement>(null)
  const condoOwnershipRef = useRef<HTMLInputElement>(null)
  const condoJuristicRef = useRef<HTMLInputElement>(null)
  const bankBookRef = useRef<HTMLInputElement>(null)

  const calculateProgress = () => {
    const documents = formData.documents || {}
    const requiredFields = [
      documents.idCard,
      documents.houseRegistration,
      documents.condoOwnership,
      documents.bankBook,
    ]

    const filledFields = requiredFields.filter((field) => field && field !== '').length
    const progressPercentage = Math.round((filledFields / requiredFields.length) * 100)
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [formData.documents, onStepProgress])

  const handleDocumentUpload = (field: string, value: string | null) => {
    const updatedDocuments = {
      ...formData.documents,
      [field]: value,
    }
    updateFormData({ documents: updatedDocuments })
  }

  const handleFileUpload = async (field: string, files: FileList | null) => {
    if (!files || files.length === 0) return

    if (!userLoggedIn?.userToken) {
      message.error('Authentication token is missing. Please log in again.')
      return
    }

    const file = files[0]
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const maxSize = 20 * 1024 * 1024

    if (!allowedTypes.includes(file.type)) {
      message.error('Please upload JPEG, PNG, or PDF files only.')
      return
    }

    if (file.size > maxSize) {
      message.error('File size must be less than 20MB.')
      return
    }

    setUploadingField(field)

    try {
      const formDataUpload = new FormData()
      formDataUpload.append('fileName', file)
      formDataUpload.append('deviceType', 'WEBSITE')
      formDataUpload.append('project', 'cms')

      const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
        method: 'POST',
        headers: {
          Authorization: userLoggedIn.userToken,
          project: 'haup-stable',
        },
        body: formDataUpload,
      })

      if (!response.ok) {
        throw new Error(`Upload failed for ${file.name}`)
      }

      const result = await response.json()
      if (result.result && result.fileUrl) {
        handleDocumentUpload(field, result.fileUrl)
        message.success(`${file.name} uploaded successfully!`)
      } else {
        throw new Error(result.message || `Upload failed for ${file.name}`)
      }
    } catch (err: any) {
      message.error(err.message || 'An error occurred during upload')
    } finally {
      setUploadingField(null)
    }
  }

  const triggerFileUpload = (field: string) => {
    if (uploadingField) return

    switch (field) {
      case 'idCard':
        idCardRef.current?.click()
        break
      case 'houseRegistration':
        houseRegistrationRef.current?.click()
        break
      case 'condoOwnership':
        condoOwnershipRef.current?.click()
        break
      case 'condoJuristic':
        condoJuristicRef.current?.click()
        break
      case 'bankBook':
        bankBookRef.current?.click()
        break
    }
  }

  const handleDragOver = (e: React.DragEvent, field: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverField(field)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverField(null)
    }
  }

  const handleDrop = (e: React.DragEvent, field: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverField(null)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleFileUpload(field, files)
    }
  }

  const isNextDisabled = progress < 100 || isSubmittingDraft

  const DocumentUploadSection = ({
    title,
    field,
    isOptional = false,
  }: {
    title: string
    field: string
    isOptional?: boolean
  }) => {
    const documents = formData.documents || {}
    const hasFile = documents[field as keyof DocumentFormData]
    const isUploading = uploadingField === field

    return (
      <div style={{ marginBottom: '24px' }}>
        <h4
          style={{
            fontSize: '14px',
            color: '#333',
            marginBottom: '12px',
            fontWeight: '500',
            margin: '0 0 12px 0',
          }}
        >
          {title}{' '}
          {isOptional && <span style={{ color: '#999', fontWeight: '400' }}>(optional)</span>}
        </h4>

        {!hasFile ? (
          <div
            onClick={() => triggerFileUpload(field)}
            onDragOver={(e) => handleDragOver(e, field)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, field)}
            style={{
              border: `2px dashed ${dragOverField === field ? '#4C4848' : '#e0e0e0'}`,
              borderRadius: '8px',
              padding: '40px 20px',
              textAlign: 'center',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              backgroundColor:
                dragOverField === field ? '#FFF8F3' : isUploading ? '#f5f5f5' : '#fafafa',
              transition: 'all 0.3s ease',
              opacity: isUploading ? 0.6 : 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <img
                src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1CucRaQpqsqxAWv2pcxRk14zzSj7ec1akHuLJRnuZSaVKt5gvm4hPYRv1aL2hbP2PEjkEqsjkTKzjxJoN2s6azQn.png"
                alt="Upload icon"
                style={{ width: '32px', height: '32px' }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: isUploading ? '#999' : dragOverField === field ? '#4C4848' : '#4C4848',
                  fontWeight: '500',
                }}
              >
                {isUploading
                  ? 'Uploading...'
                  : dragOverField === field
                  ? 'Drop file here'
                  : 'Upload, take a photo or drag & drop'}
              </span>
            </div>
          </div>
        ) : (
          <div
            style={{
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#f0f0f0',
            }}
          >
            {hasFile.toLowerCase().includes('.pdf') ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #22c55e',
                  borderRadius: '8px',
                }}
              >
                <div style={{ marginBottom: '8px' }}>📄</div>
                <p
                  style={{
                    color: '#16a34a',
                    fontSize: '14px',
                    margin: '0',
                    fontWeight: '500',
                  }}
                >
                  ✓ PDF uploaded successfully
                </p>
              </div>
            ) : (
              <img
                src={hasFile}
                alt={title}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}

            <button
              onClick={() => handleDocumentUpload(field, null)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>
        )}

        <input
          ref={
            field === 'idCard'
              ? idCardRef
              : field === 'houseRegistration'
              ? houseRegistrationRef
              : field === 'condoOwnership'
              ? condoOwnershipRef
              : field === 'condoJuristic'
              ? condoJuristicRef
              : field === 'bankBook'
              ? bankBookRef
              : null
          }
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleFileUpload(field, e.target.files)}
          style={{ display: 'none' }}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <DocumentUploadSection title="ID card" field="idCard" />
        <DocumentUploadSection title="House registration book" field="houseRegistration" />
        <DocumentUploadSection title="Condo ownership certificate" field="condoOwnership" />
        <DocumentUploadSection title="Bank book" field="bankBook" />
        <DocumentUploadSection title="Condo juristic approval" field="condoJuristic" isOptional />

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: !isNextDisabled ? '0 2px 8px rgba(183, 135, 90, 0.3)' : 'none',
            }}
            onMouseDown={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(0.95)'
              }
            }}
            onMouseUp={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
            onMouseLeave={(e) => {
              if (!isNextDisabled) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            {isSubmittingDraft ? 'Publishing...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step4
